import type { DsarStatus } from '../dataProvider/types';

/**
 * Statutory-deadline helpers and display vocabulary for the DSAR queue (D-dsar, the Phase 7
 * lead). The deadline is DERIVED off the injected clock, the established ticket-SLA /
 * suspension-elapse / impersonation-expiry pattern: a requestedAt plus a fixed statutory
 * window gives a due instant, and the clock places a request on_track / approaching / breached.
 * NO stored deadline state and NO enforcement engine: the worklist surfaces the urgency, a
 * human works it.
 */

const DAY = 86_400_000;

/**
 * The statutory window a DSAR must be fulfilled within, in milliseconds. [REVIEW] the length
 * with counsel and the compliance team: 30 days is the common data-subject-access standard, but
 * the exact obligation is the platform's to confirm, so it lives here as one adjustable constant.
 */
export const STATUTORY_WINDOW_MS = 30 * DAY;

/**
 * How long before the statutory due instant a request counts as APPROACHING (the amber band).
 * A named constant so the threshold is one obvious place to tune, not a magic number inline.
 */
export const DEADLINE_APPROACHING_MS = 7 * DAY;

/** The ISO instant a request lodged at `requestedAt` is statutorily due. */
export const dueAt = (requestedAt: string): string =>
  new Date(new Date(requestedAt).getTime() + STATUTORY_WINDOW_MS).toISOString();

/**
 * The derived deadline state for an UNRESOLVED request (received / in_progress), the suspension
 * phaseFor shape rather than the ticket boolean (compliance grades urgency in three bands, not
 * two):
 *  - breached: the clock is past the statutory due instant, still unresolved. Unmissable.
 *  - approaching: within DEADLINE_APPROACHING_MS of due, not yet breached.
 *  - on_track: more than the approaching window remains.
 * A resolved request (fulfilled / rejected) has no live deadline, so the model carries null.
 */
export type DeadlinePhase = 'on_track' | 'approaching' | 'breached';

export const deadlinePhase = (
  requestedAt: string,
  now: number,
): DeadlinePhase => {
  const due = new Date(dueAt(requestedAt)).getTime();
  if (now > due) return 'breached';
  if (now >= due - DEADLINE_APPROACHING_MS) return 'approaching';
  return 'on_track';
};

/** Human label per DSAR status. */
export const DSAR_STATUS_LABEL: Record<DsarStatus, string> = {
  received: 'Received',
  in_progress: 'In progress',
  fulfilled: 'Fulfilled',
  rejected: 'Rejected',
};

export const DSAR_STATUS_COLOR: Record<
  DsarStatus,
  'default' | 'info' | 'success' | 'error'
> = {
  received: 'info',
  in_progress: 'default',
  fulfilled: 'success',
  rejected: 'error',
};

/** Chip label per derived deadline phase. */
export const DEADLINE_PHASE_LABEL: Record<DeadlinePhase, string> = {
  on_track: 'On track',
  approaching: 'Due soon',
  breached: 'Statutory deadline breached',
};

export const DEADLINE_PHASE_COLOR: Record<
  DeadlinePhase,
  'default' | 'warning' | 'error'
> = {
  on_track: 'default',
  approaching: 'warning',
  breached: 'error',
};
